'use client'

import { useMemo, useState } from 'react'
import { useRouter } from 'next/navigation'

import { AppShell } from '@/components/layout/AppShell'
import { PageHeader } from '@/components/layout/PageHeader'
import { EmptyState } from '@/components/common/EmptyState'
import { LoadingSpinner } from '@/components/common/LoadingSpinner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useI18n } from '@/lib/hooks/use-i18n'
import { useMarketplace } from '@/lib/hooks/use-marketplace'
import type { AppItem } from '@/lib/types/marketplace'
import { useToast } from '@/lib/hooks/use-toast'
import { AppCard } from './components/AppCard'
import { Store, RefreshCw, Search, X } from 'lucide-react'

function resolveLabel(t: (key: string) => string, key: string, fallback: string) {
  const label = t(key)
  return label === key ? fallback : label
}

function pickDownloadUrl(downloadInfo: AppItem['launch_info']): string | null {
  if (!downloadInfo) return null

  const platform = typeof navigator !== 'undefined' ? navigator.platform.toLowerCase() : ''
  if (platform.includes('win')) return downloadInfo.windows || null
  if (platform.includes('mac')) return downloadInfo.mac || null
  if (platform.includes('android')) return downloadInfo.android || null
  if (platform.includes('iphone') || platform.includes('ipad') || platform.includes('ios')) {
    return downloadInfo.ios || null
  }
  return downloadInfo.windows || downloadInfo.mac || downloadInfo.android || downloadInfo.ios || null
}

export default function MarketplacePage() {
  const { t } = useI18n()
  const { toast } = useToast()
  const router = useRouter()
  const { data: apps, isLoading, isError, isFetching, refetch } = useMarketplace()

  const [searchKeyword, setSearchKeyword] = useState('')
  const [selectedCategory, setSelectedCategory] = useState<string>('all')

  const categories = useMemo(() => {
    const unique = new Set<string>()
    ;(apps ?? []).forEach((app) => {
      if (app.category) unique.add(app.category)
    })
    return Array.from(unique).sort((a, b) => a.localeCompare(b))
  }, [apps])

  const filteredApps = useMemo(() => {
    const keyword = searchKeyword.trim().toLowerCase()
    let result = apps ?? []

    if (keyword) {
      result = result.filter((app) => {
        const haystack = `${app.name} ${app.description}`.toLowerCase()
        return haystack.includes(keyword)
      })
    }

    if (selectedCategory !== 'all') {
      result = result.filter((app) => app.category === selectedCategory)
    }

    return result
  }, [apps, searchKeyword, selectedCategory])

  const handleLaunch = (app: AppItem) => {
    toast({
      title: t('marketplace.launch'),
      description: t('marketplace.launch.toast', { name: app.name }),
    })
      console.log(app)
    if (app.launch_type === 'inner') {
        if (app.launch_info?.url) {
            router.push(`/marketplace${app.launch_info.url}`)
        }
    } else if (app.launch_type === 'external') {
        if (app.launch_info?.url) {
            window.open(app.launch_info.url, '_blank', 'noopener,noreferrer')
        }
    }

  }

  const handleDownload = (app: AppItem) => {
    if (app.launch_type !== 'download') {
      toast({
        title: t('marketplace.download'),
        description: t('marketplace.download.missing'),
        variant: 'destructive',
      })
      return
    }

    const url = pickDownloadUrl(app.launch_info)
    if (!url) {
      toast({
        title: t('marketplace.download'),
        description: t('marketplace.download.unsupported'),
        variant: 'destructive',
      })
      return
    }

    window.open(url, '_blank', 'noopener,noreferrer')
    toast({
      title: t('marketplace.download'),
      description: t('marketplace.download.started'),
    })
  }

  return (
    <AppShell>
      <div className="flex flex-col h-full">
        <PageHeader title={t('marketplace.title')} />

        <div className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-6">

            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-3 flex-wrap">
                <div className="relative w-72 max-w-full">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    value={searchKeyword}
                    onChange={(e) => setSearchKeyword(e.target.value)}
                    placeholder={t('marketplace.searchPlaceholder')}
                    className="pl-9 pr-9"
                  />
                  {searchKeyword && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                      onClick={() => setSearchKeyword('')}
                      aria-label={t('marketplace.searchClear')}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>

                <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                  <SelectTrigger className="w-44">
                    <SelectValue placeholder={t('marketplace.categoryPlaceholder')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('marketplace.category.all')}</SelectItem>
                    {categories.map((category) => (
                      <SelectItem key={category} value={category}>
                        {resolveLabel(t, `marketplace.category.${category}`, category)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Button
                size="icon"
                variant="outline"
                onClick={() => refetch()}
                disabled={isFetching}
                aria-label={t('marketplace.refresh')}
              >
                <RefreshCw className={isFetching ? 'h-4 w-4 animate-spin' : 'h-4 w-4'} />
              </Button>
            </div>

            {isLoading ? (
              <div className="flex items-center justify-center min-h-[40vh]">
                <LoadingSpinner size="lg" />
              </div>
            ) : isError ? (
              <EmptyState
                icon={Store}
                title={t('marketplace.loadError.title')}
                description={t('marketplace.loadError.description')}
                action={
                  <Button onClick={() => refetch()} variant="outline">
                    {t('marketplace.retry')}
                  </Button>
                }
              />
            ) : filteredApps.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 pb-6">
                {filteredApps.map((app) => (
                  <AppCard
                    key={app.id}
                    app={app}
                    onLaunch={handleLaunch}
                    onDownload={handleDownload}
                  />
                ))}
              </div>
            ) : (
              <EmptyState
                icon={Store}
                title={t('marketplace.empty.title')}
                description={t('marketplace.empty.description')}
              />
            )}
          </div>
        </div>
      </div>
    </AppShell>
  )
}
